import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { IoArrowBackOutline } from "react-icons/io5";
import axios from "axios";
import "../css/Charts.css";
import "../css/mainhome.css";

const API_BASE_URL = "https://admin.gama567.club/api/v1";
const VALID_CHART_TYPES = new Set(["game", "starline", "jackpot"]);

function displayChartName(chartName) {
  if (!chartName) return "CHART";

  try {
    return decodeURIComponent(chartName).replace(/-/g, " ").toUpperCase();
  } catch {
    return chartName.replace(/-/g, " ").toUpperCase();
  }
}

function parseChartDate(value) {
  const match = String(value ?? "").match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) {
    return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])).getTime();
  }

  const parsed = Date.parse(String(value ?? ""));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function normalizeRow(row) {
  return {
    date: String(row?.date ?? "-"),
    open: String(row?.open ?? "-"),
    digit: String(row?.digit ?? "-"),
    close: String(row?.close ?? "-"),
  };
}

export default function ChartDetail() {
  const { gameType: routeGameType, gameId, chartName } = useParams();
  const navigate = useNavigate();
  const normalizedType = String(routeGameType ?? "game").toLowerCase();
  const gameType = VALID_CHART_TYPES.has(normalizedType) ? normalizedType : "game";
  const numericGameId = Number(gameId);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emptyMessage, setEmptyMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      setEmptyMessage("");

      if (!Number.isInteger(numericGameId) || numericGameId <= 0) {
        setData([]);
        setError("Invalid chart link.");
        setLoading(false);
        return;
      }

      try {
        const headers = {
          "Content-Type": "application/json",
          deviceId: "qwert",
          deviceName: "sm2233",
          accessStatus: "1",
        };

        const response = await axios.post(
          `${API_BASE_URL}/table-chart`,
          {
            gameId: numericGameId,
            gameType,
          },
          {
            headers,
            signal: controller.signal,
          }
        );

        if (response.data?.status === false) {
          if (active) {
            setData([]);
            setEmptyMessage(response.data?.msg || "No chart data available.");
          }
          return;
        }

        if (!Array.isArray(response.data?.info)) {
          throw new Error(response.data?.msg || "Invalid chart response.");
        }

        const rows = response.data.info
          .map(normalizeRow)
          .sort((left, right) => parseChartDate(left.date) - parseChartDate(right.date));

        if (active) {
          setData(rows);
        }
      } catch (err) {
        if (!active || axios.isCancel(err)) return;
        setData([]);
        setError(err.response?.data?.msg || err.message || "Network error. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
      controller.abort();
    };
  }, [gameType, numericGameId, retryCount]);

  return (
    <div className="mainhome-screen-wrapper">
      <div className="charts-header screen-header">
        <button className="back-btn" onClick={() => navigate(-1)} aria-label="Back">
          <IoArrowBackOutline size={24} />
        </button>
        <h2>{displayChartName(chartName)}</h2>
      </div>

      <div className="chart-table-container screen-content">
        <div className="chart-table-header">
          <span>Date</span>
          <span>Open</span>
          <span>Jodi</span>
          <span>Close</span>
        </div>

        {loading && <p className="chart-message">Loading chart...</p>}
        {!loading && error && (
          <div className="chart-message-panel">
            <p className="chart-message error" role="alert">{error}</p>
            <button type="button" className="chart-retry-btn" onClick={() => setRetryCount(value => value + 1)}>
              Try again
            </button>
          </div>
        )}
        {!loading && !error && data.length === 0 && (
          <p className="chart-message">{emptyMessage || "No chart data available."}</p>
        )}

        <div className="chart-table-body">
          {data.map((row, idx) => (
            <div key={`${row.date}-${idx}`} className="chart-row">
              <span>{row.date}</span>
              <span>{row.open}</span>
              <span className="jodi">{row.digit}</span>
              <span>{row.close}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
